import React, { Dispatch, SetStateAction } from "react";

import { Flex, Modal, Table } from "antd";
import TextArea from "antd/es/input/TextArea";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import i18next from "i18next";
import styled from "styled-components";

import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
} from "api/reactQuery/querySchemas";
import Card from "components/Card/Card";

import WarehouseCard from "./components/WarehouseCard";

type Props = {
  isOpen: boolean;
  onSubmit: () => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
  data: BSCoreServicesDtoMarketplaceOrderDto;
  setOrderComment: Dispatch<SetStateAction<string>>;
  selectedDepots: BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[];
};

const OrderPreviewModal = ({
  isOpen,
  onSubmit,
  isLoading,
  onCancel,
  data,
  setOrderComment,
  selectedDepots,
}: Props) => {
  const cols: ColumnsType = [
    {
      dataIndex: "name",
      key: "name",
      title: i18next.t("order_preview_modal_name"),
    },
    {
      dataIndex: "expiryDate",
      key: "expiryDate",
      title: i18next.t("order_preview_expiry_date"),
    },
    {
      dataIndex: "productPrice",
      key: "productPrice",
      title: i18next.t("order_preview_product_price"),
    },
    {
      dataIndex: "unitsInPallet",
      key: "unitsInPallet",
      title: i18next.t("order_preview_units_in_pallet"),
    },
    {
      dataIndex: "productVolumeInHL",
      key: "productVolumeInHL",
      title: i18next.t("product_volume_in_hl"),
    },
    {
      dataIndex: "productWeight",
      key: "productWeight",
      title: i18next.t("product_weight"),
    },
    {
      dataIndex: "orderedQuantitiesInPallets",
      key: "orderedQuantitiesInPallets",
      title: i18next.t("ordered_quantities_in_pallets"),
    },
    {
      dataIndex: "orderedQuantitiesInUnits",
      key: "orderedQuantitiesInUnits",
      title: i18next.t("ordered_quantites_in_units"),
    },
    {
      dataIndex: "orderedUnits",
      key: "orderedUnits",
      title: i18next.t("ordered_units"),
    },
    {
      dataIndex: "totalBeerAndPackagingCostWithVat",
      key: "totalBeerAndPackagingCostWithVat",
      title: i18next.t("total_beer_and_packaging_cost_with_vat"),
    },
  ];

  return (
    <Modal
      open={isOpen}
      onOk={onSubmit}
      confirmLoading={isLoading}
      onCancel={onCancel}
      width="90%"
      okText={i18next.t("send_order")}
      cancelText={i18next.t("back_button")}
    >
      <Heading>{i18next.t("order_overview")}</Heading>
      <Section>
        <TitleAndDateWrapper>
          <SectionTitle>{i18next.t("order_placement")}</SectionTitle>
          <p>
            {i18next.t("delivery_date")}{" "}
            <span>{dayjs(data?.deliveryDate).format("DD.MM.YYYY")}</span>
          </p>
        </TitleAndDateWrapper>
        <WarehouseCardWrapper>
          {selectedDepots.map((depot) => (
            <WarehouseCard key={depot.id} depot={depot} />
          ))}
        </WarehouseCardWrapper>
      </Section>

      <Section>
        <SectionTitle>{i18next.t("products")}</SectionTitle>
        {data?.childOrders?.map((order) => {
          return (
            <div key={order.id}>
              <DepotName>{order.depotName}</DepotName>
              {
                <div>
                  <Table
                    columns={cols}
                    pagination={false}
                    dataSource={order?.products
                      ?.filter(({ parentId, isBonusInvoiceProduct, id, isSplitProduct}) => {
                        if (isBonusInvoiceProduct && parentId === null && !isSplitProduct) {
                          return false
                        }
                        
                        if (id) {
                          return true
                        }

                        return false
                      })
                      .map((product) => {
                        return {
                          expiryDate: product.expiryDate
                            ? dayjs(product.expiryDate).format("DD.MM.YY")
                            : "",
                          isBonusInvoiceProduct: product.isBonusInvoiceProduct,
                          key: product.feTableId.toString(),
                          name: product.productName,
                          orderedQuantitiesInPallets:
                            product.orderedQuantitiesInPallets,
                          orderedQuantitiesInUnits:
                            product.orderedQuantitiesInUnits,
                          orderedUnits: product.orderedUnits,
                          parentId: product.parentId,
                          productPrice: product.productPrice,
                          productVolumeInHL: product.productVolumeInHL,
                          productWeight: product.productWeight,
                          totalBeerAndPackagingCostWithVat:
                            product.totalBeerAndPackagingCostWithVat,
                          unitsInPallet: product.productUnitsInPallet,
                        };
                      })}
                  />
                </div>
              }
              <CardWrapper>
                <Card
                  title={i18next.t("ttl_weight")}
                  value={order.total.ttlWeight}
                />
                <Card
                  title={i18next.t("total_beer_cost_with_vat_in_bgn")}
                  value={order.total.ttlBeerCostWithVATBGN}
                  isBlueCard={true}
                />
              </CardWrapper>
            </div>
          );
        })}
      </Section>

      {!!data?.total && (
        <Section>
          <SectionTitle>{i18next.t("total")}</SectionTitle>
          <Flex style={{ gap: 10, width: "100%" }} justify="space-between">
            <Card
              title={i18next.t("ttl_pallets")}
              value={data.total.ttlPallets}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_pallet_count_licensed")}
              value={data.total.ttlPalletCountLicensedType}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_pallet_count_industrial")}
              value={data.total.ttlPalletCountIndustrialType}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_hl")}
              value={data.total.ttlVolumeInHL}
              width="25%"
            />
          </Flex>

          <Flex
            style={{ gap: 10, marginTop: 10, width: "100%" }}
            justify="space-between"
          >
            <Card
              title={i18next.t("ttl_cost_without_vat")}
              value={data.total.ttlBeerCostWithoutVATBGN}
              width="25%"
            />
            <Card
              title={i18next.t("total_beer_and_packaging_cost_with_vat")}
              value={data.total.ttlBeerWithVATAndPackagingCostInBGN}
              width="25%"
            />
            <Card
              title={i18next.t("total_packaging_cost_in_bgn")}
              value={data.total.ttlPackagingCostInBGN}
              width="25%"
            />
            <Card
              title={i18next.t("ttl_weight")}
              value={data.total.ttlWeight}
              width="25%"
            />
          </Flex>
        </Section>
      )}

      <Section>
        <SectionTitle>{i18next.t("order_comment")}</SectionTitle>
        <TextArea
          placeholder={i18next.t("order_comment_placeholder")}
          onChange={(e) => setOrderComment(e.target.value)}
          maxLength={200}
        />
      </Section>
    </Modal>
  );
};

export default OrderPreviewModal;

const Section = styled.div`
  margin-top: 38px;
`;

const SectionTitle = styled.h2`
  color: #6d6d6d;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const WarehouseCardWrapper = styled.div`
  display: flex;
  gap: 20px;
`;

const Heading = styled.h1`
  color: #363636;
  font-size: 32px;
  font-weight: 400;
`;

const TitleAndDateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  & > p {
    color: #6d6d6d;

    & > span {
      font-weight: bold;
    }
  }
`;

const DepotName = styled.h3`
  font-weight: 400;
  font-size: 18px;
  color: #181818;
  margin: 20px 0;
`;

const CardWrapper = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  gap: 15px;
`;
